import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BrainCircuit, FileText, Globe, Loader2, Sparkles, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Field, Input, Textarea } from "@/components/FormBits";
import { api } from "@/lib/api";

const SOURCE_ICON = { upload: FileText, paste: FileText, url: Globe };

export default function AdminKnowledge() {
  const [samples, setSamples] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [training, setTraining] = useState(false);
  const [pasteTitle, setPasteTitle] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [url, setUrl] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/admin/knowledge").then((r) => {
      setSamples(r.data.samples || []);
      setProfile(r.data.profile || null);
    }).finally(() => setLoading(false));
  }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/knowledge/samples", fd);
      setSamples((s) => [data.sample, ...s]);
      toast.success(`Added "${data.sample.title}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onPaste = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/knowledge/samples/text", { title: pasteTitle, text: pasteText });
      setSamples((s) => [data.sample, ...s]);
      setPasteTitle(""); setPasteText("");
      toast.success("Text sample added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add text");
    } finally { setBusy(false); }
  };

  const onImportUrl = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/knowledge/samples/url", { url });
      setSamples((s) => [data.sample, ...s]);
      setUrl("");
      toast.success(`Imported "${data.sample.title}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/admin/knowledge/samples/${id}`);
      setSamples((s) => s.filter((x) => x.id !== id));
    } catch { toast.error("Could not delete sample"); }
  };

  const onTrain = async () => {
    setTraining(true);
    try {
      const { data } = await api.post("/admin/knowledge/train", {}, { timeout: 120000 });
      setProfile(data.profile);
      toast.success("Company voice learned — all AI writing now follows it");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Training failed");
    } finally { setTraining(false); }
  };

  const onClearProfile = async () => {
    try {
      await api.delete("/admin/knowledge/profile");
      setProfile(null);
      toast.success("Voice profile cleared — AI reverts to the default house style");
    } catch { toast.error("Could not clear profile"); }
  };

  if (loading) return <Layout><div className="py-20 text-center text-gray-500">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <Link to="/admin" className="rcw-btn-ghost text-sm mb-4" data-testid="knowledge-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to projects
        </Link>
        <div className="flex items-center gap-3">
          <BrainCircuit className="h-8 w-8 text-amber-500" />
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900">AI Voice Training</h1>
        </div>
        <p className="text-sm text-gray-500 mt-2 max-w-2xl">
          Upload previous case studies, brochures or website pages. The AI studies how your company
          writes and applies that exact voice to every enhancement, article and social post it generates.
        </p>

        {profile ? (
          <div className="rcw-card p-6 mt-6 border-l-4 border-amber-400" data-testid="voice-profile-card">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-xl font-semibold flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-amber-500" /> Company voice active
                </h2>
                <p className="text-xs text-gray-400 mt-1">
                  Trained {profile.trained_at ? new Date(profile.trained_at).toLocaleString("en-GB") : ""} from {profile.sample_count} sample{profile.sample_count === 1 ? "" : "s"}
                </p>
              </div>
              <button onClick={onClearProfile} className="rcw-btn-ghost text-sm text-red-600" data-testid="clear-profile-btn">
                <Trash2 className="h-4 w-4" /> Clear profile
              </button>
            </div>
            <div className="mt-4 space-y-3 text-sm text-gray-700">
              <p data-testid="voice-tone-summary"><strong>Tone:</strong> {profile.tone_summary}</p>
              {profile.sentence_style && <p><strong>Style:</strong> {profile.sentence_style}</p>}
              {profile.structural_habits && <p><strong>Structure:</strong> {profile.structural_habits}</p>}
              {!!(profile.vocabulary || []).length && (
                <div className="flex flex-wrap gap-1.5">
                  {profile.vocabulary.map((v) => (
                    <span key={v} className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-xs border border-amber-200">{v}</span>
                  ))}
                </div>
              )}
              {!!(profile.avoid || []).length && (
                <div className="flex flex-wrap gap-1.5">
                  {profile.avoid.map((v) => (
                    <span key={v} className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs border border-red-200 line-through">{v}</span>
                  ))}
                </div>
              )}
              {profile.guidance && <p className="text-gray-500 italic border-l-2 border-gray-200 pl-3">{profile.guidance}</p>}
            </div>
          </div>
        ) : (
          <div className="rcw-card p-6 mt-6 bg-gray-50" data-testid="no-profile-card">
            <p className="text-sm text-gray-600">
              No voice profile yet — the AI is using the default house style. Add samples below, then press <strong>Learn company voice</strong>.
            </p>
          </div>
        )}

        <div className="rcw-card p-6 mt-6 space-y-6" data-testid="add-samples-card">
          <h2 className="font-display text-xl font-semibold">Add writing samples</h2>

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Upload a document</p>
              <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-xl p-6 cursor-pointer hover:border-amber-400 transition-colors">
                <Upload className="h-6 w-6 text-gray-400" />
                <span className="text-sm text-gray-500">PDF, DOCX, TXT or MD</span>
                <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden" onChange={onUpload} data-testid="knowledge-file-input" />
              </label>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Import a webpage</p>
              <div className="flex gap-2">
                <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://yoursite.co.uk/case-study" data-testid="knowledge-url-input" />
                <button onClick={onImportUrl} disabled={busy || !url.trim()} className="rcw-btn-ghost text-sm shrink-0" data-testid="knowledge-url-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />} Import
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">e.g. a project page or news article from your website.</p>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Or paste text</p>
            <div className="space-y-2">
              <Input value={pasteTitle} onChange={(e) => setPasteTitle(e.target.value)} placeholder="Title (e.g. 2023 Capability Brochure)" data-testid="knowledge-paste-title" />
              <Textarea rows={4} value={pasteText} onChange={(e) => setPasteText(e.target.value)} placeholder="Paste a case study, brochure copy or article here…" data-testid="knowledge-paste-text" />
              <button onClick={onPaste} disabled={busy || pasteText.trim().length < 100} className="rcw-btn-ghost text-sm" data-testid="knowledge-paste-btn">
                Add text sample
              </button>
              {pasteText.trim().length > 0 && pasteText.trim().length < 100 && (
                <p className="text-xs text-gray-400">Minimum 100 characters ({pasteText.trim().length}/100)</p>
              )}
            </div>
          </div>
        </div>

        <div className="rcw-card p-6 mt-6" data-testid="samples-list-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-xl font-semibold">Samples ({samples.length})</h2>
            <button onClick={onTrain} disabled={training || samples.length === 0} className="rcw-btn-amber text-sm" data-testid="train-voice-btn">
              {training ? <><Loader2 className="h-4 w-4 animate-spin" /> Learning your voice…</> : <><Sparkles className="h-4 w-4" /> {profile ? "Re-learn company voice" : "Learn company voice"}</>}
            </button>
          </div>
          {samples.length === 0 ? (
            <p className="text-sm text-gray-400 mt-4">No samples yet — add at least one above.</p>
          ) : (
            <ul className="mt-4 divide-y divide-gray-100">
              {samples.map((s) => {
                const Icon = SOURCE_ICON[s.source] || FileText;
                return (
                  <li key={s.id} className="py-3 flex items-center gap-3" data-testid={`sample-row-${s.id}`}>
                    <Icon className="h-4 w-4 text-gray-400 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 truncate">{s.title}</p>
                      <p className="text-xs text-gray-400">
                        {s.source === "url" ? s.url : s.filename || "Pasted text"} · {(s.chars || 0).toLocaleString()} chars
                      </p>
                    </div>
                    <button onClick={() => onDelete(s.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors" data-testid={`delete-sample-${s.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Layout>
  );
}
